import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../i18n';

const CONSENT_KEY = 'alb_cookie_consent';
const VISITOR_KEY = 'alb_analytics_visitor';
const SESSION_KEY = 'alb_analytics_session';

function analyticsAllowed() {
    try {
        return JSON.parse(localStorage.getItem(CONSENT_KEY))?.analytics === true;
    } catch {
        return false;
    }
}

function identifier(storage, key) {
    let value = storage.getItem(key);
    if (!value) {
        value = crypto.randomUUID();
        storage.setItem(key, value);
    }
    return value;
}

export function AnalyticsTracker() {
    const location = useLocation();
    const { locale } = useI18n();
    const [enabled, setEnabled] = useState(analyticsAllowed);
    const startedAt = useRef(Date.now());
    const pageCount = useRef(1);
    const started = useRef(false);
    const sessionId = useRef(null);
    const entryPath = useRef(`${location.pathname}${location.search}`);
    const entryLocale = useRef(locale);

    useEffect(() => {
        const updateConsent = (event) => setEnabled(event.detail?.analytics === true);
        window.addEventListener('alb-cookie-consent', updateConsent);
        return () => window.removeEventListener('alb-cookie-consent', updateConsent);
    }, []);

    useEffect(() => {
        if (!enabled) {
            started.current = false;
            sessionStorage.removeItem(SESSION_KEY);
            return undefined;
        }
        const visitorId = identifier(localStorage, VISITOR_KEY);
        sessionId.current = identifier(sessionStorage, SESSION_KEY);
        startedAt.current = Date.now();
        pageCount.current = 1;
        api.post('/analytics/session/start', {
            visitor_id: visitorId,
            session_id: sessionId.current,
            path: entryPath.current,
            referrer: document.referrer,
            locale: entryLocale.current,
        }).then(() => { started.current = true; }).catch(() => {});
        return undefined;
    }, [enabled]);

    useEffect(() => {
        if (!enabled || !sessionId.current) return;
        pageCount.current += 1;
    }, [enabled, location.pathname, location.search]);

    useEffect(() => {
        if (!enabled) return undefined;
        const heartbeat = () => {
            if (!started.current || !sessionId.current) return;
            api.post('/analytics/session/heartbeat', {
                session_id: sessionId.current,
                duration_seconds: Math.round((Date.now() - startedAt.current) / 1000),
                page_count: pageCount.current,
                path: `${location.pathname}${location.search}`,
            }).catch(() => {});
        };
        const interval = window.setInterval(heartbeat, 15000);
        const onVisibilityChange = () => { if (document.visibilityState === 'hidden') heartbeat(); };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            heartbeat();
        };
    }, [enabled, location.pathname, location.search]);

    return null;
}
